import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SoundProvider } from "@/contexts/SoundContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import LobbyPage from "@/pages/LobbyPage";
import GamePage from "@/pages/GamePage";
import ProfilePage from "@/pages/ProfilePage";
import SettingsPage from "@/pages/SettingsPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import RulesPage from "@/pages/RulesPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import Footer from "@/components/Footer";
import DailyChallengePage from "@/pages/DailyChallengePage";
import SeasonPassPage from "@/pages/SeasonPassPage";
import SpectatorPage from "@/pages/SpectatorPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If user data passed from AuthCallback, render immediately
  if (location.state?.user) return children;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#38B6FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Check URL fragment for session_id BEFORE routes render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <LobbyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <LobbyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/game/:gameId"
        element={
          <ProtectedRoute>
            <ErrorBoundary scope="GamePage">
              <GamePage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/daily-challenge"
        element={
          <ProtectedRoute>
            <ErrorBoundary scope="DailyChallengePage">
              <DailyChallengePage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/season-pass"
        element={
          <ProtectedRoute>
            <SeasonPassPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spectate/:gameId"
        element={
          <ProtectedRoute>
            <ErrorBoundary scope="SpectatorPage">
              <SpectatorPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <LeaderboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rules"
        element={
          <ProtectedRoute>
            <RulesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/privacy"
        element={
          <ProtectedRoute>
            <PrivacyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/terms"
        element={
          <ProtectedRoute>
            <TermsPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

/**
 * Skip-to-main-content link (KNOWN_ISSUES §1.47).
 * Visually hidden until focused; keyboard users hit Tab once to
 * reveal it and Enter to jump past the page chrome to the
 * `id="main-content"` landmark each page exposes.
 */
function SkipToMainLink() {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[9999] focus:rounded-md focus:bg-[#1A237E] focus:text-white focus:px-3 focus:py-2 focus:font-semibold focus:shadow-lg"
      data-testid="skip-to-main-link"
    >
      Skip to main content
    </a>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem
        storageKey="mm.theme"
        disableTransitionOnChange
      >
        <BrowserRouter>
          <AuthProvider>
            <SoundProvider>
              <SkipToMainLink />
              <AppRouter />
              {/* Global footer. Self-suppresses on /game/ and
                  /spectate/ routes where vertical space is precious
                  and a footer would push the hand row off-screen. */}
              <Footer />
              <Toaster position="top-center" richColors closeButton />
            </SoundProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
